import has from 'lodash/has';
import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import {
  getVaultOrgProducts,
  getVaultOrgs,
  IVaultOrg,
} from '../api/vault/orgs';
import {
  getSecurityProfiles as getSecurityProfilesFromApi,
  ISecurityProfile,
} from '../api/vault/securityProfiles';
import { SelectOptionType } from '../utils/selection';

// for react-select
export type _ISecurityProfileOption = SelectOptionType<string>;

// for mui-select
export interface ISecurityProfileOption {
  value: string;
  label: string;
  _id: string;
  cluster: boolean;
  group: string;
}

export type GroupOptions = Array<{
  label: string;
  options: Array<_ISecurityProfileOption>;
}>

export enum ActionType {
  AddOptions = 'SecurityProfiles.AddOptions',
  AddProfileIdValue = 'SecurityProfiles.AddProfileIdValue',
  MarkLoaded = 'SecurityProfiles.MarkLoaded',
  UpdateProductOptions = 'SecurityProfiles.UpdateProductOptions',
  UpdateSecurityProfile = 'SecurityProfiles.UpdateSecurityProfile',
}

export interface IState {
  isLoaded: boolean;
  securityProfileOptions: Array<ISecurityProfileOption>;
  options: Array<_ISecurityProfileOption>;
  productOptions: GroupOptions,
  idValueMapping: Array<ISecurityProfile>;
}

const INITIAL_STATE: IState = {
  isLoaded: false,
  securityProfileOptions: [],
  // note: for react-select
  options: [],
  productOptions: [],
  idValueMapping: [],
};

export interface ISecurityProfilesAction extends Action {
  payload?: any;
}

export default function reducer(state: IState = INITIAL_STATE, action: ISecurityProfilesAction): IState {
  switch (action.type) {
    case ActionType.AddOptions:
      return {
        ...state,
        securityProfileOptions: action.payload.securityProfileOptions,
        options: action.payload.options,
      };
    case ActionType.AddProfileIdValue:
      return {
        ...state,
        idValueMapping: action.payload.idValueMapping,
      };
    case ActionType.MarkLoaded:
      return {
        ...state,
        isLoaded: true,
      };
    case ActionType.UpdateProductOptions:
      return {
        ...state,
        productOptions: action.payload.productOptions,
      };
    case ActionType.UpdateSecurityProfile:
      return {
        ...state,
        idValueMapping: (state.idValueMapping || []).reduce((container, securityProfile) => {
          if (action.payload.securityProfile._id === securityProfile._id) {
            container.push(action.payload.securityProfile);
          }
          container.push(securityProfile);
          return container;
        }, []),
      };
    default:
      return state;
  }
}

function sortOptions(a: ISecurityProfileOption, b: ISecurityProfileOption) {
  if (a.cluster === b.cluster) {
    return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
  }
  return (a.cluster && !b.cluster) ? -1 : 1;
}

export function securityProfilesInformation(source: Array<ISecurityProfile>): {
  securityProfileOptions: Array<ISecurityProfileOption>,
  securityProfiles: Array<ISecurityProfile>,
} {
  let securityProfileOptions: Array<ISecurityProfileOption> = [];
  const securityProfiles: Array<ISecurityProfile> = [];
  if (!source) {
    return { securityProfileOptions, securityProfiles };
  }
  source.forEach((securityProfile) => {
    const {
      name, _id, idm_org_id, idm_org_collection,
    } = securityProfile;
    if (name && _id && (idm_org_id || idm_org_collection)) {
      const isCluster = idm_org_collection != null && idm_org_collection.length > 0;
      securityProfileOptions.push({
        value: name, label: name, _id, cluster: isCluster, group: isCluster ? 'GROUPS' : 'ORGANIZATIONS',
      });
      securityProfiles.push(securityProfile);
    }
  });
  securityProfileOptions = securityProfileOptions.sort(sortOptions);
  return { securityProfileOptions, securityProfiles };
}

export const getSecurityProfiles = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const response = await getSecurityProfilesFromApi();
    const {
      securityProfileOptions,
      securityProfiles,
    } = securityProfilesInformation(response.data.data);
    const options = securityProfileOptions.map(({ label, _id }) => ({ label, value: _id }));

    dispatch({
      type: ActionType.AddOptions,
      payload: {
        securityProfileOptions,
        options,
      },
    });

    dispatch({
      type: ActionType.AddProfileIdValue,
      payload: {
        idValueMapping: securityProfiles,
      },
    });

    dispatch({ type: ActionType.MarkLoaded });
  } catch (e) {
    console.error('[QA] failed to load security profiles', e.toString());
  }
};

const optionFromSp = (sp: ISecurityProfile): _ISecurityProfileOption => ({
  value: sp._id,
  label: sp.name,
});

export const getSecurityProfileOptions = async (state: IAppState): Promise<GroupOptions> => {
  const {
    product: {
      target: product,
    },
    securityProfiles: {
      idValueMapping,
    },
  } = state;

  const productOrgs: Array<IVaultOrg> = [];

  try {
    const response = await getVaultOrgs(product);
    productOrgs.push(...(response.data?.data || []));
  } catch (e) {
    console.error(`[QA] failed to find "${product}" organizations`, e.toString());
  }

  const profiles = idValueMapping
    .filter((sp) => productOrgs.some((org) => org.idm_org_id === sp.idm_org_id))
    .sort((a, b) => {
      const aCluster = a.idm_org_collection?.length > 0;
      const bCluster = b.idm_org_collection?.length > 0;
      if (aCluster === bCluster) {
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      }
      return (aCluster && !bCluster) ? -1 : 1;
    });

  const productOptions = [
    {
      label: 'Groups',
      options: profiles
        .filter((sp) => sp.idm_org_collection && sp.idm_org_collection.length > 0)
        .map((sp) => optionFromSp(sp)),
    },
    {
      label: 'Organizations',
      options: profiles
        .filter((sp) => !sp.idm_org_collection || (sp.idm_org_collection && sp.idm_org_collection.length === 0))
        .map((sp) => optionFromSp(sp)),
    },
  ];

  return productOptions;
};

export const getSecurityProfileProductOptions = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  const productOptions = await getSecurityProfileOptions(state);

  dispatch({
    type: ActionType.UpdateProductOptions,
    payload: {
      productOptions,
    },
  });
};

export function selectSecurityProfileId(state: IAppState) {
  const securityProfilesMapping = state.securityProfiles?.idValueMapping ?? [];
  const userVaultRoles = state.user?.userOrgRole?.loginAs?.userVaultRoles;
  const organizationId = userVaultRoles?.[0]?.orgId ?? -1;
  const securityProfile = securityProfilesMapping.find((sp) => sp.idm_org_id === organizationId);
  if (securityProfile) {
    const { _id } = securityProfile;
    return _id;
  }
  return null;
}

export const selectSecurityProfileOptions = (state: IAppState) => state.securityProfiles.securityProfileOptions;

export const updateSecurityProfile = (securityProfile: ISecurityProfile) => ({
  type: ActionType.UpdateSecurityProfile,
  payload: {
    securityProfile,
  },
});

export const idsToSecurityProfileNames = (ids: string[], securityProfiles: ISecurityProfile[]) => {
  const names: string[] = [];
  ids.forEach((id) => {
    const found = securityProfiles.find((sp) => sp._id === id);
    if (found) names.push(found.name);
  });
  return names.join(', ');
};
